import React, { PureComponent } from "react";
import ReactEcharts from "echarts-for-react";
import styled from "styled-components";
import { Chart, ChartProps, ColorProps, GlobalColorPalette, Color, ChartStyleProps, ChartHighLightProps,
  ChartHighLight, getRangeByIndex } from "@gago/frame";
import tinycolor from "tinycolor2";
import { uniqueId } from "lodash";
// tslint:disable:ter-max-len completed-docs cyclomatic-complexity prefer-for-of variable-name jsx-no-multiline-js
const McBarChartBox = styled.div<{chartStyle: ChartStyleProps["chartStyle"]}>`
  width: 100%;
  height: 100%;
  position: relative;
  .y-label{
    position: absolute;
    top: 0px;
    left: 0px;
    font-size: ${props => props.chartStyle.secondaryTextSize}px;
    color: ${props => props.chartStyle.secondaryTextColor};
  }
  .legend-box{
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    .legend-single{
      display: flex;
      justify-content: flex-start;
      align-items: center;
      margin-right: 12px;
      &:last-of-type{
        margin-right: 0px;
      }
      .ls-icon{
        height: 8px;
        width: 8px;
        border-radius: 50%;
        background-color: transparent;
      }
      .ls-label{
        margin-left: 6px;
        font-size: ${props => props.chartStyle.secondaryTextSize}px;
        color: ${props => props.chartStyle.secondaryTextColor};
      }
    }
  }
`;

type MProps= {
  /** 控制Y轴分级线的条数 */
  ySplitNumber: number;
  /** 柱状图的宽度 */
  barWidth: number;
  /** 多组柱状图柱的距离 */
  barGap: number;
} & ChartProps<2, 1> & ColorProps<GlobalColorPalette> & ChartStyleProps & ChartHighLightProps;
interface Mstate {}

/**
 * 堆叠类型（允许多类型）图表
 * @author 马俊峰
 * @date 2020-06-28
 * @export
 * @class McBarChart
 * @extends {PureComponent<MProps>}
 * @implements {Chart<2, 1>}
 * @implements {Color}
 */
export class McBarChart extends PureComponent<MProps, Mstate> implements Chart<2, 1>, Color, ChartHighLight {
  /** 获取echart dom */
  echartRef: any = undefined;
  static defaultProps = {
    ySplitNumber: 5,
    barWidth: 12,
    barGap: 0.2,
    chartHighLight: undefined,
    onChartClick() {},
    onChartHover() {},
  };
  render() {
    const { colorPalette, chartData: { head, range, data, suffix }, chartStyle } = this.props;
    const { subColor } = colorPalette;
    if (data.length < 1) { return "data no found"; }
    const legendRange = Array.from(new Set([...range[1], ...data.map<string>(item => item[1])]));
    const xData = Array.from(new Set([...range[2], ...data.map<string>(item => item[2])]));
    const yData: any = {};
    for (const item of data) {
      if (!yData[item[1]]) { yData[item[1]] = []; }
      const indexof = xData.indexOf(item[2]) || 0;
      [yData[item[1]][indexof]] = [item[3]];
    }
    const defaultOption = this.getDefaultOption(legendRange, xData, yData);
    return(
      <McBarChartBox chartStyle={chartStyle}>
        <div className="y-label" >{`${head[3]}：${suffix && suffix[3]}`}</div>
        <div className="legend-box">
          {legendRange.length > 1 && legendRange.map((item, index) => (
            <div className="legend-single" key={index}>
              <span className="ls-icon" style={{ backgroundColor: subColor[index] }} />
              <span className="ls-label">{item}</span>
            </div>
          ))}
        </div>
        <ReactEcharts
          notMerge
          onChartReady={this.onChartReady}
          style={{ height: "100%", width: "100%" }}
          option={defaultOption}
        />
      </McBarChartBox>
    );
  }
  /** 添加点击和悬停事件监听 */
  onChartReady = (chart: any) => {
    const cagtegoryRange = getRangeByIndex(this.props.chartData, 2);
    chart.off("click");
    chart.off("mouseover");
    chart.off("mouseout");
    chart.on("click", (e: any) => this.props.onChartClick(cagtegoryRange[e.dataIndex]));
    chart.on("mouseover", (e: any) => this.props.onChartHover(cagtegoryRange[e.dataIndex]));
    chart.on("mouseout", () => this.props.onChartHover(undefined));
  }
  /** 处理生成defaultOption对象 */
  getDefaultOption = (legendList: string[], xData: string[], yData: any) => {
    const { colorPalette: { subColor }, chartStyle, chartData: { suffix, range }, ySplitNumber, barWidth, barGap, chartHighLight } = this.props;
    const defaultOption = {
      color: subColor,
      grid: {
        containLabel: true,
        borderWidth: 0,
        top: legendList.length > 3 ? 50 : 30,
        bottom: 1,
        left: 1,
        right: 1,
        textStyle: {
          color: chartStyle.secondaryTextColor,
        },
      },
      tooltip: {
        trigger: "axis",
        confine: true,
        axisPointer: {
          type: "shadow",
        },
        backgroundColor: "rgba(0, 0, 0, 0.65)",
        textStyle: {
          align: "left",
          fontSize: 12,
          color: chartStyle.tooltipMainTextColor,
        },
        extraCssText: "box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.2)", // 添加阴影
        formatter: (toolItems: any[]) => {
          const params = toolItems;
          let detail = `<div style="font-size: 14px">${params[0] ? params[0].name : ""}</div>`;
          for (let num = 0; num < params.length; num++) {
            const { seriesName, value } = params[num];
            detail = detail + `
            <div style="font-size: 12px">
                <span style="display:inline-block;margin-right:6px;width:8px;height:8px;background-color:${subColor[num]};"></span>
                <span>${seriesName}：${value && value || "0"} ${suffix && suffix[3]}</span>
            </div>
            `;
          }
          return detail;
        },
      },
      legend: {
        show: false,
        icon: "circle",
        width: "65%",
        itemWidth: 8,
        itemHeight: 8,
        top: 0,
        right: 2,
        textStyle: {
          fontSize: 12,
          color: chartStyle.secondaryTextColor,
          padding: [0, 0, 0, 2],
        },
        lineStyle: {
          width: 1.2,
        },
        data: legendList,
      },
      xAxis: {
        show: true,
        type: "category",
        axisTick: {
          show: false,
        },
        axisPointer: {},
        axisLine: {
          show: true,
          lineStyle: {
            color: chartStyle.mainAxisColor,
          },
        },
        axisLabel: {
          show: true,
          textStyle: {
            fontSize: chartStyle.secondaryTextSize,
            color: chartStyle.secondaryTextColor,
          },
        },
        splitLine: { show: false },
        data: xData,
      },
      yAxis: {
        show: true,
        type: "value",
        max: (range[3].length > 0) ? Math.max(...range[3]) : null,
        min: (range[3].length > 0) ? Math.min(...range[3]) : null,
        splitNumber: ySplitNumber,
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dotted",
            color: chartStyle.splitLineColor,
          },
        },
        axisLabel: {
          textStyle: {
            fontSize: chartStyle.secondaryTextSize,
            color: chartStyle.secondaryTextColor,
          },
        },
      },
      series: [] as any[],
    };
    if (chartHighLight !== undefined) {
      defaultOption.xAxis.axisPointer = {
        show:  true,
        value: chartHighLight,
        type: "shadow",
        label: {
          show: false,
        },
        handle: {
          show: true,
          color: "rgba(255,255,255,0)",
        },
      };
    }
    for (let index = 0; index < legendList.length; index++) {
      const element = legendList[index];
      if (!yData[element]) { console.warn("图表类型范围不符！"); }
      const serieItem = [
        {
          barGap,
          barWidth,
          name: element,
          type: "bar",
          stack: element,
          barMinHeight: 3,
          itemStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                    { offset: 0, color: `${tinycolor(subColor[index]).setAlpha(0.7).toRgbString()}` },
                    { offset: 1, color: `${tinycolor(subColor[index]).setAlpha(0.1).toRgbString()}` },
              ],
              global: false, // 缺省为 false
                /** tooltip的marker的颜色 */
              toString: () => subColor[index],
            },
          },
          data: yData[element].map((item: number) => {
            return {
              label: {
                show: true,
                formatter: (params: any) => { return "一"; },
                fontSize: 2,
                color: "transparent",
                backgroundColor: subColor[index],
                lineHeight: barWidth,
                align: "center",
                position: "top",
                distance: 3,
                borderRadius: 0,
                rotate: 90,
                offset: [barWidth / 2, 0],
              },
              value: item,
            };
          }),
        },
      ];
      defaultOption.series.push(...serieItem);
    }
    return defaultOption;
  }
}
